import React from 'react'
import styled from 'styled-components'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { getMapsUrl } from '@src/utils/maps'
import { colors, starOrbs } from '@src/theme/sharedComponents'
import designConfig from '@design-config'
import { formatPhone } from '@src/utils/phoneFormat'

const HeroSection = styled.header<{ $stars?: boolean }>`
  background: ${colors.primaryDark};
  border-bottom: 3px solid ${colors.primary};
  padding: 3rem 2rem;
  color: white;
  position: relative;
  overflow: hidden;
  ${p => p.$stars && starOrbs}
`

const HeroInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`

const HeroEyebrow = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 0.5rem;
`

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  margin-bottom: 0.6rem;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1;
`

const HeroSubtitle = styled.p`
  font-size: 0.95rem;
  opacity: 0.65;
  max-width: 560px;
  margin: 0 0 2rem;
`

const ContactMethodsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const ContactMethod = styled.a`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
  padding: 0.6rem 1rem;
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.75);
  font-size: 0.82rem;
  font-weight: 600;
  transition: border-color 0.15s, color 0.15s;

  i { color: ${colors.accent}; font-size: 0.95rem; }
  &:hover { border-color: ${colors.accent}; color: white; }
`

const ContactPageHeader: React.FC = () => {
  const { data: s } = usePublicSettings()

  return (
    <HeroSection $stars={designConfig.themeDecorations[s?.theme_id ?? ''] === 'stars'}>
      <HeroInner>
        {s?.contact_page_eyebrow && <HeroEyebrow>{s.contact_page_eyebrow}</HeroEyebrow>}
        <HeroTitle>{s?.contact_page_title ?? 'Contact Us'}</HeroTitle>
        <HeroSubtitle>{s?.contact_page_subtitle ?? "Have questions? Our team is here to help."}</HeroSubtitle>
        <ContactMethodsRow>
          {s?.contact_phone && (
            <ContactMethod href={`tel:${s.contact_phone.replace(/\D/g, '')}`}><i className="bi bi-telephone" />{formatPhone(s.contact_phone)}</ContactMethod>
          )}
          {s?.contact_email && (
            <ContactMethod href={`mailto:${s.contact_email}`}><i className="bi bi-envelope" />{s.contact_email}</ContactMethod>
          )}
          {(s?.contact_region || s?.contact_address_city) && (
            <ContactMethod href={s ? getMapsUrl(s) ?? undefined : undefined} target="_blank" rel="noopener noreferrer">
              <i className="bi bi-geo-alt" />{s?.contact_region || s?.contact_address_city}
            </ContactMethod>
          )}
          {s?.contact_hours && (
            <ContactMethod as="div" style={{ cursor: 'default' }}><i className="bi bi-clock" />{s.contact_hours}</ContactMethod>
          )}
        </ContactMethodsRow>
      </HeroInner>
    </HeroSection>
  )
}

export default ContactPageHeader
